import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import {
  getSupportBookedEventLogs,
  getSupportVideoSessionTokens,
  sendSupportBookedEventJoinLink,
} from 'src/supportPortal/services/supportBookedEventActionsService'

dayjs.extend(utc)

const SUPPORT_LOG_ACTION_LABELS = {
  SUPPORT_CANCELLED: 'Cancelled',
  SUPPORT_RESCHEDULED: 'Rescheduled',
  SUPPORT_REASSIGNED: 'Reassigned',
  SUPPORT_MARKED_COMPLETED: 'Marked completed',
  SUPPORT_MARKED_PAID: 'Marked paid',
  SUPPORT_MARKED_TEST: 'Marked as test',
  SUPPORT_UNMARKED_TEST: 'Test flag removed',
  SUPPORT_RECORDING_ENABLED: 'Recording enabled',
  SUPPORT_RECORDING_DISABLED: 'Recording disabled',
  SUPPORT_JOIN_LINK_SENT: 'Join link sent',
  RTC_PROVIDER_OVERRIDE_UPDATED: 'Video provider changed',
}

function formatInstant(value, fallback = '—') {
  if (!value) {
    return fallback
  }
  return dayjs.utc(value).local().format('DD MMM YYYY, h:mm A')
}

function SectionCard({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: 'rgba(148, 163, 184, 0.26)' }}>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 800,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          mb: 1.25,
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  )
}

function DetailRow({ label, value, valueColor = '#0f172a' }) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 0.4 }}>
      <Typography sx={{ fontSize: 12.5, color: '#64748b', minWidth: 128, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: valueColor, fontWeight: 600, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  )
}

function CopyableLink({ label, url }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>{label}</Typography>
        <Typography sx={{ fontSize: 11.5, color: '#2563eb', wordBreak: 'break-all' }}>{url}</Typography>
      </Box>
      <Tooltip title="Copy link">
        <IconButton
          size="small"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url)
              toast.success('Link copied')
            } catch (error) {
              toast.error('Could not copy the link')
            }
          }}
        >
          <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export default function SupportBookedEventDetailDrawer({ open, onClose, bookedEvent }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [tokens, setTokens] = useState([])
  const [supportLogs, setSupportLogs] = useState([])
  const [loadingExtras, setLoadingExtras] = useState(false)
  const [resendingKind, setResendingKind] = useState('')

  const isOnline = bookedEvent?.meetingSrc === 'aumhum'

  useEffect(() => {
    if (!open || !bookedEvent?.id) {
      return undefined
    }
    let cancelled = false

    async function loadExtras() {
      setLoadingExtras(true)
      const [tokenList, logsResult] = await Promise.all([
        isOnline ? getSupportVideoSessionTokens([bookedEvent.id]) : Promise.resolve([]),
        getSupportBookedEventLogs(bookedEvent.id),
      ])
      if (cancelled) {
        return
      }
      setTokens(tokenList || [])
      setSupportLogs(
        (logsResult?.logs || []).filter(
          (logEntry) =>
            logEntry?.role === 'support' || SUPPORT_LOG_ACTION_LABELS[logEntry?.action],
        ),
      )
      setLoadingExtras(false)
    }

    void loadExtras()
    return () => {
      cancelled = true
    }
  }, [bookedEvent?.id, isOnline, open])

  const patientName = bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Patient'
  const eventTypeLabel =
    displayNamesOfEventTypes?.[bookedEvent?.eventType] || bookedEvent?.eventType || 'Session'

  const rescheduleHistory = useMemo(
    () =>
      (bookedEvent?.modificationHistory || []).filter(
        (item) => item?.event === 'booking-rescheduled',
      ),
    [bookedEvent?.modificationHistory],
  )

  const practitionerJoinUrl = bookedEvent?.slug ? `https://aumhum.com/l/${bookedEvent.slug}` : null

  async function handleResend(kind) {
    setResendingKind(kind)
    const result = await sendSupportBookedEventJoinLink({
      bookedEventId: bookedEvent?.id,
      kind,
    })
    setResendingKind('')
    if (result.ok) {
      toast.success(`${kind === 'secure' ? 'Secure' : 'Public'} join link sent to the patient`)
    } else {
      toast.error(result.message)
    }
  }

  const statusChips = []
  if (bookedEvent?.status) {
    statusChips.push({ label: bookedEvent.status, color: bookedEvent.status === 'cancelled' ? 'error' : 'default' })
  }
  if (bookedEvent?.paymentStatus) {
    statusChips.push({
      label: `payment: ${bookedEvent.paymentStatus}`,
      color: bookedEvent.paymentStatus === 'paid' ? 'success' : 'warning',
    })
  }
  if (bookedEvent?.sessionStatus) {
    statusChips.push({ label: bookedEvent.sessionStatus, color: 'info' })
  }
  if (bookedEvent?.testEvent) {
    statusChips.push({ label: 'test booking', color: 'warning' })
  }

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 520,
          maxWidth: '100%',
          height: isMobile ? '92vh' : '100vh',
          borderTopLeftRadius: isMobile ? 20 : 0,
          borderTopRightRadius: isMobile ? 20 : 0,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
            background: 'linear-gradient(180deg, rgba(37, 99, 235, 0.05) 0%, rgba(255, 255, 255, 0.98) 100%)',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <EventNoteRoundedIcon sx={{ color: '#2563eb', fontSize: 22 }} />
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }} noWrap>
                  {patientName}
                </Typography>
              </Stack>
              <Typography sx={{ color: '#475569', fontSize: 13 }}>
                {eventTypeLabel} with {bookedEvent?.mentorName || 'unassigned'}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                {statusChips.map((chip) => (
                  <Chip key={chip.label} label={chip.label} size="small" color={chip.color} variant="outlined" />
                ))}
              </Stack>
            </Box>
            <IconButton onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>
          <Stack spacing={2}>
            <SectionCard title="Schedule">
              <DetailRow label="Starts" value={formatInstant(bookedEvent?.startTime)} />
              <DetailRow label="Ends" value={formatInstant(bookedEvent?.endTime)} />
              <DetailRow label="Mode" value={isOnline ? 'Online (video)' : 'In person'} />
              <DetailRow label="Booked on" value={formatInstant(bookedEvent?.createdTime)} />
              {bookedEvent?.markedCompleted ? (
                <DetailRow
                  label="Completed at"
                  value={formatInstant(bookedEvent?.markedCompletedTime)}
                  valueColor="#047857"
                />
              ) : null}
              {bookedEvent?.status === 'cancelled' ? (
                <>
                  <DetailRow
                    label="Cancelled at"
                    value={formatInstant(bookedEvent?.cancelledTime)}
                    valueColor="#b91c1c"
                  />
                  <DetailRow label="Cancelled by" value={bookedEvent?.cancelledBy} valueColor="#b91c1c" />
                </>
              ) : null}
            </SectionCard>

            <SectionCard title="Payment">
              <DetailRow
                label="Fee"
                value={
                  bookedEvent?.feePaid
                    ? `${bookedEvent?.currency || 'INR'} ${bookedEvent.feePaid}`
                    : 'Not recorded'
                }
              />
              <DetailRow label="Payment status" value={bookedEvent?.paymentStatus} />
              <DetailRow label="Collected by" value={bookedEvent?.feeCollectionDetails?.collectedBy} />
              <DetailRow label="Collection note" value={bookedEvent?.feeCollectionDetails?.notes} />
              {bookedEvent?.adminConfirmPayment ? (
                <>
                  <Divider sx={{ my: 1 }} />
                  <DetailRow
                    label="Support confirmed"
                    value={bookedEvent.adminConfirmPayment.reasonGiven}
                    valueColor="#047857"
                  />
                  <DetailRow label="Mode used" value={bookedEvent.adminConfirmPayment.paymentGatewayUsed} />
                  <DetailRow label="Txn reference" value={bookedEvent.adminConfirmPayment.paymentTxnId} />
                </>
              ) : null}
            </SectionCard>

            <SectionCard title="Clinic">
              <DetailRow
                label="Brand"
                value={bookedEvent?.clinicBrandData?.brandName || bookedEvent?.clinicBrandData?.title}
              />
              <DetailRow
                label="Location"
                value={bookedEvent?.clinicLocation?.title || bookedEvent?.clinicLocation?.name}
              />
              <DetailRow label="Room" value={bookedEvent?.chamberRoomName} />
              <DetailRow label="Token number" value={bookedEvent?.appointmentNumber} />
            </SectionCard>

            {isOnline ? (
              <SectionCard title="Joining Links">
                <Stack spacing={1.25}>
                  {practitionerJoinUrl ? (
                    <CopyableLink label="Practitioner / session link" url={practitionerJoinUrl} />
                  ) : null}
                  {tokens.map((token) => (
                    <CopyableLink
                      key={token.id || token.sessionSlug}
                      label={`Patient session link${token.enabled === false ? ' (disabled)' : ''}${
                        token.expiresAt ? ` · expires ${formatInstant(token.expiresAt)}` : ''
                      }`}
                      url={`https://aumhum.com/v/${token.sessionSlug}`}
                    />
                  ))}
                  {loadingExtras && tokens.length === 0 ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={14} />
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>Loading links…</Typography>
                    </Stack>
                  ) : null}
                  <Divider />
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={Boolean(resendingKind)}
                      onClick={() => handleResend('secure')}
                    >
                      {resendingKind === 'secure' ? 'Sending…' : 'Resend secure link'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      disabled={Boolean(resendingKind)}
                      onClick={() => handleResend('public')}
                    >
                      {resendingKind === 'public' ? 'Sending…' : 'Resend public link'}
                    </Button>
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>
                    Links are sent to the patient&apos;s WhatsApp and recorded in the activity trail.
                  </Typography>
                </Stack>
              </SectionCard>
            ) : null}

            {rescheduleHistory.length > 0 ? (
              <SectionCard title={`Reschedule History (${rescheduleHistory.length})`}>
                <Stack spacing={1}>
                  {rescheduleHistory.map((item, index) => (
                    <Box
                      key={`${item?.modifiedTime || index}`}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(241, 245, 249, 0.75)',
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
                        {formatInstant(item?.previousStartTime)} → {formatInstant(item?.newStartTime)}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                        {formatInstant(item?.modifiedTime)}
                        {item?.modifiedBy ? ` · by ${item.modifiedBy}` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </SectionCard>
            ) : null}

            <SectionCard title="Support Activity">
              {loadingExtras ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={14} />
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>Loading activity…</Typography>
                </Stack>
              ) : supportLogs.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>
                  No support actions recorded on this booking yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {supportLogs.map((logEntry, index) => (
                    <Box
                      key={logEntry?.id || index}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(239, 246, 255, 0.7)',
                        border: '1px solid rgba(191, 219, 254, 0.7)',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8' }}>
                          {SUPPORT_LOG_ACTION_LABELS[logEntry?.action] || logEntry?.action}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>
                          {formatInstant(logEntry?.createdTime)}
                        </Typography>
                      </Stack>
                      {logEntry?.note ? (
                        <Typography sx={{ fontSize: 11.5, color: '#475569', mt: 0.25 }}>
                          {logEntry.note}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Stack>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid rgba(148, 163, 184, 0.2)',
            backgroundColor: '#ffffff',
          }}
        >
          <Stack direction="row" justifyContent="flex-end">
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  )
}
